/**
 * Rascunho local da tela de criação de movimento.
 *
 * O estado da tela (OS selecionada, equipamentos/insumos bipados, volumes e
 * reservas de carregamento) vive apenas no React. Sem persistência, um reload
 * (F5, queda de conexão, etc.) perde o estado da tela mas mantém os
 * equipamentos em AGUARDANDO CARREGAMENTO no backend.
 *
 * Este módulo guarda esse estado no localStorage. A chave é por usuário +
 * organização, então o mesmo rascunho é retomado ao recarregar a página.
 */

const DRAFT_KEY_PREFIX = 'movement_create_draft'
const DRAFT_VERSION = 1
const SESSION_EXPIRED_KEY = 'movement_create_session_expired'
const PREPARE_LOGOUT_EVENT = 'movement:create:prepare-logout'

// Rascunhos abandonados são descartados depois desse período. Na leitura de
// um rascunho expirado, o hook libera as reservas que ficaram órfãs.
export const MOVEMENT_DRAFT_TTL_MS = 24 * 60 * 60 * 1000

export const getMovementDraftKey = (userId, organizationId) => {
  if (userId == null || organizationId == null) return null
  return `${DRAFT_KEY_PREFIX}:${userId}:${organizationId}`
}

/**
 * A organização selecionada fica no sessionStorage (OrganProvider). Lemos
 * direto dele porque, no primeiro render da página, o contexto pode ainda
 * estar com selectedOrgan = null.
 */
export const getSessionOrganizationId = () => {
  try {
    const raw = sessionStorage.getItem('selectedOrgan')
    if (!raw) return null
    const organ = JSON.parse(raw)
    return organ?.id ?? null
  } catch (error) {
    console.error('Erro ao ler organização da sessão:', error)
    return null
  }
}

export const readMovementDraft = (key) => {
  if (!key) return null

  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null

    const draft = JSON.parse(raw)

    if (!draft || draft.version !== DRAFT_VERSION) {
      localStorage.removeItem(key)
      return null
    }

    const isExpired =
      !draft.savedAt || Date.now() - draft.savedAt > MOVEMENT_DRAFT_TTL_MS

    if (isExpired) {
      localStorage.removeItem(key)
      return { ...draft, expired: true }
    }

    return draft
  } catch (error) {
    console.error('Erro ao ler rascunho de movimento:', error)
    try {
      localStorage.removeItem(key)
    } catch (removeError) {
      console.error('Erro ao descartar rascunho inválido:', removeError)
    }
    return null
  }
}

export const saveMovementDraft = (key, data) => {
  if (!key) return false

  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        ...data,
        version: DRAFT_VERSION,
        savedAt: Date.now(),
      })
    )
    return true
  } catch (error) {
    // Quota cheia ou modo privado: não vale derrubar a tela por causa do
    // rascunho; o fluxo normal continua funcionando sem persistência.
    console.error('Erro ao salvar rascunho de movimento:', error)
    return false
  }
}

export const clearMovementDraft = (key) => {
  if (!key) return

  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error('Erro ao limpar rascunho de movimento:', error)
  }
}

export const persistActiveMovementDraftBeforeLogout = () => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(PREPARE_LOGOUT_EVENT))
}

export const hasMovementDraftForUser = (userId = null) => {
  let currentUserId = userId

  if (currentUserId == null) {
    try {
      currentUserId = JSON.parse(localStorage.getItem('user') || 'null')?.id
    } catch (error) {
      return false
    }
  }

  if (currentUserId == null) return false
  const draftPrefix = `${DRAFT_KEY_PREFIX}:${currentUserId}:`
  return Object.keys(localStorage).some((key) => key.startsWith(draftPrefix))
}

/**
 * Mantém apenas os rascunhos do usuário atual para recuperação após logout.
 * Os demais dados locais continuam sendo removidos como no logout normal.
 */
export const preserveMovementDraftsForRecovery = (userId = null) => {
  let currentUserId = userId

  if (currentUserId == null) {
    try {
      currentUserId = JSON.parse(localStorage.getItem('user') || 'null')?.id
    } catch (error) {
      console.error('Erro ao identificar usuário do rascunho:', error)
    }
  }

  const draftPrefix = currentUserId == null
    ? null
    : `${DRAFT_KEY_PREFIX}:${currentUserId}:`
  const drafts = []

  if (draftPrefix) {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(draftPrefix)) {
        drafts.push([key, localStorage.getItem(key)])
      }
    })
  }

  localStorage.clear()
  drafts.forEach(([key, value]) => localStorage.setItem(key, value))
  if (drafts.length > 0) {
    markMovementSessionExpired()
  } else {
    clearMovementSessionExpired()
  }
  return drafts.length > 0
}

// Compatibilidade com os fluxos existentes de expiração de sessão.
export const preserveMovementDraftsOnSessionExpiry = preserveMovementDraftsForRecovery

export const markMovementSessionExpired = () => {
  try {
    sessionStorage.setItem(SESSION_EXPIRED_KEY, 'true')
  } catch (error) {
    console.error('Erro ao marcar expiração da sessão de movimento:', error)
  }
}

export const wasMovementSessionExpired = () => {
  try {
    return sessionStorage.getItem(SESSION_EXPIRED_KEY) === 'true'
  } catch (error) {
    return false
  }
}

export const clearMovementSessionExpired = () => {
  try {
    sessionStorage.removeItem(SESSION_EXPIRED_KEY)
  } catch (error) {
    console.error('Erro ao limpar marca de expiração da sessão:', error)
  }
}

export const getMovementDraftForRecovery = (userId, organizationId) => {
  if (!wasMovementSessionExpired()) return null

  const draft = readMovementDraft(getMovementDraftKey(userId, organizationId))
  if (!draft) clearMovementSessionExpired()
  return draft
}
